/**
 * Bonus widget — Conversations Meta non lues (Dashboard home).
 *
 * Affiche les 5 derniers threads Meta non lus avec :
 *  - Avatar + canal (WA/FB/IG) + sender + preview
 *  - Bouton 1-clic "Tout assigner à moi" (assigne tous les threads non assignés)
 *  - CTA "Ouvrir l'inbox"
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { MessageCircle, UserPlus, ChevronRight, Loader2, Inbox } from "lucide-react";
import { Button } from "../ui/button";

const CHANNEL_BG = { whatsapp: "#25d366", messenger: "#0084ff", instagram: "#e1306c" };
const CHANNEL_LABEL = { whatsapp: "WA", messenger: "FB", instagram: "IG" };

function formatRelTime(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  } catch { return ""; }
}

export default function MetaInboxWidget() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [stats, setStats] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/meta/threads?unread_only=true&limit=5"),
        api.get("/meta/status").catch(() => ({ data: null })),
      ]);
      setThreads(r1.data?.threads || []);
      if (r2.data) setStats(r2.data.stats || null);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const assignAllToMe = async () => {
    if (!window.confirm("Assigner toutes les conversations non assignées à vous ?")) return;
    setAssigning(true);
    try {
      const r = await api.post("/meta/threads/assign-all-to-me");
      alert(`${r.data?.assigned || 0} conversation(s) assignée(s).`);
      await reload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 animate-fade-in-up" data-testid="meta-inbox-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Messagerie Meta</h3>
            <p className="text-[10px] text-slate-500">WhatsApp · Messenger · Instagram</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/admin/messagerie")}
          className="text-[10px] font-medium hover:underline text-blue-400 inline-flex items-center gap-0.5"
          data-testid="widget-meta-open"
        >
          Ouvrir l'inbox <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <StatBox value={stats.threads || 0} label="Total" color="#94a3b8" />
          <StatBox value={stats.unread || 0} label="Non lus" color="#f59e0b" highlight={stats.unread > 0} />
          <StatBox value={stats.total_messages || 0} label="Messages" color="#3b82f6" />
        </div>
      )}

      {/* Threads list */}
      {loading ? (
        <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" /></div>
      ) : threads.length === 0 ? (
        <div className="py-6 text-center" data-testid="widget-empty">
          <Inbox className="w-8 h-8 mx-auto mb-1.5 text-slate-700" />
          <p className="text-xs text-slate-500">Aucune conversation non lue</p>
          <p className="text-[10px] text-slate-600 mt-0.5">Tout est sous contrôle 🎯</p>
        </div>
      ) : (
        <>
          <ul className="space-y-1.5 mb-3">
            {threads.map((t) => {
              const bg = CHANNEL_BG[t.channel] || "#64748b";
              return (
                <li key={t.thread_id}>
                  <button
                    onClick={() => navigate("/admin/messagerie")}
                    className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                    data-testid={`widget-thread-${t.thread_id}`}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: bg }}>
                      {(t.sender_name || t.sender_id || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-xs font-medium truncate">{t.sender_name || t.sender_id}</span>
                        <span className="text-[9px] text-slate-500 flex-shrink-0">{formatRelTime(t.last_message_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] px-1 rounded font-bold" style={{ color: bg, backgroundColor: `${bg}22` }}>{CHANNEL_LABEL[t.channel]}</span>
                        <span className="text-[10px] text-slate-400 truncate">{t.last_message_preview || "(pièce jointe)"}</span>
                        {t.unread_count > 0 && (
                          <span className="ml-auto bg-violet-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                            {t.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Assign-all-to-me CTA */}
          <Button
            onClick={assignAllToMe}
            disabled={assigning}
            variant="outline"
            size="sm"
            className="w-full border-blue-500/30 bg-blue-500/5 text-blue-300 hover:bg-blue-500/15 text-[11px] h-8"
            data-testid="widget-meta-assign-all"
          >
            {assigning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
            M'assigner les non assignées
          </Button>
        </>
      )}
    </div>
  );
}

function StatBox({ value, label, color, highlight }) {
  return (
    <div className={`bg-[#0b0811] rounded-lg p-2 text-center ${highlight ? "ring-1 ring-amber-500/40" : ""}`}>
      <div className="text-base font-black" style={{ color }}>{value}</div>
      <div className="text-[9px] text-slate-500 uppercase">{label}</div>
    </div>
  );
}
